import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Wand2 } from "lucide-react";
import ContractsList from "@/components/contracts/ContractsList";

const SAMPLE = `# Host Agreement
This Host Agreement is between Freshy Fresh Productions LLC ("Company") and the Host named below.

## 1. Duties
- Host will stream their assigned show(s) on the published schedule.
- Host will follow every Company Policy published at /policies.

## 2. Confidentiality
Host agrees to keep the terms of this agreement and all internal Company information confidential.

## 3. Acknowledgements
[ ] Host has read and agrees to all Company Policies
[ ] Host agrees to the payout schedule
[ ] Company will promote the Host's show on official channels

## Signatures
COMPANY
Founder Signature: ______________________   Date: __________

HOST
Host Signature: ______________________   Date: __________
Printed Name: ______________________
`;

const FILTERS = [["all", "All"], ["draft", "Drafts"], ["sent", "Awaiting"], ["completed", "Completed"], ["voided", "Voided"]];

function NewContract({ onCreated }) {
  const [mode, setMode] = useState("upload");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!title.trim()) return toast.error("Give the contract a title");
    setBusy(true);
    try {
      let data;
      if (mode === "upload") {
        if (!file) { toast.error("Choose a PDF"); return; }
        const fd = new FormData();
        fd.append("title", title.trim());
        fd.append("file", file);
        ({ data } = await api.post("/contracts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post("/contracts/text", { title: title.trim(), body }));
      }
      toast.success(`Created — ${data.fields.length} field(s) auto-detected`);
      onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-6 space-y-4" data-testid="new-contract">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="label-eyebrow">▮ NEW CONTRACT</div>
        <div className="flex gap-px bg-[var(--ff-border)]">
          {[["upload", "Upload PDF", Upload], ["text", "Write Text", FileText]].map(([m, l, Icon]) => (
            <button key={m} onClick={() => setMode(m)} className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5 ${mode === m ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)] hover:text-white"}`} data-testid={`new-contract-mode-${m}`}>
              <Icon size={12} /> {l}
            </button>
          ))}
        </div>
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Contract title (e.g. 2026 Host Agreement — Jane)" className="w-full px-3 py-2 text-sm" data-testid="new-contract-title" />
      {mode === "upload" ? (
        <label className="block border border-dashed border-[var(--ff-border)] p-6 text-center cursor-pointer hover:border-[var(--ff-red)] transition-colors" data-testid="new-contract-dropzone">
          <Upload size={20} className="mx-auto mb-2 text-[var(--ff-red)]" />
          <div className="text-sm">{file ? file.name : "Choose a PDF (max 15MB, 30 pages)"}</div>
          <div className="text-xs text-[var(--ff-text-dim)] mt-1">Signature lines, date lines, name lines and checkboxes are detected automatically.</div>
          <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="new-contract-file" />
        </label>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--ff-text-dim)]">
            <span>Use <code className="text-white">___</code> after a label (Signature, Date, Name, Initials) for fill-ins and <code className="text-white">[ ]</code> for checkboxes. Mention Founder / Host / Co-Host nearby to pick the signer. <code className="text-white"># </code> = heading.</span>
            <button onClick={() => setBody(SAMPLE)} className="btn-ghost inline-flex items-center gap-1.5 text-[0.65rem] !px-3 !py-1.5" data-testid="new-contract-sample"><Wand2 size={11} /> Insert Sample</button>
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="w-full px-3 py-2 text-sm font-mono-ui leading-relaxed" placeholder="Write or paste the contract…" data-testid="new-contract-body" />
        </div>
      )}
      <button onClick={create} disabled={busy} className="btn-primary" data-testid="new-contract-create">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Create &amp; Detect Fields
      </button>
    </div>
  );
}

export default function ContractsAdmin() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.get("/contracts").then(({ data }) => setList(data.contracts)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const shown = (list || []).filter((c) => filter === "all" || c.status === filter);
  return (
    <div className="space-y-6" data-testid="contracts-admin">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ CONFIDENTIAL · FOUNDER &amp; EXECUTIVE MANAGERS</div>
        <h2 className="font-display text-3xl">Host Contracts</h2>
        <p className="text-[var(--ff-text-dim)] text-sm mt-1 max-w-2xl">Upload or write a contract, check the auto-detected fields, assign the Host (and optional Co-Host), then send. Signed PDFs can be downloaded and printed here.</p>
      </div>
      <NewContract onCreated={(id) => nav(`/contracts/${id}`)} />
      <div className="flex flex-wrap gap-px bg-[var(--ff-border)] w-fit">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] ${filter === v ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)] hover:text-white"}`} data-testid={`contracts-filter-${v}`}>
            {l}{list ? ` · ${v === "all" ? list.length : list.filter((c) => c.status === v).length}` : ""}
          </button>
        ))}
      </div>
      <ContractsList contracts={list ? shown : null} empty="No contracts here yet." />
    </div>
  );
}
